import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Users, Building2, Briefcase } from 'lucide-react';

/**
 * SCREEN 1: "Choose how to enter" (INTENT)
 * 
 * Purpose: Capture who the user is before asking anything else.
 * Entry choice happens BEFORE identity is established.
 * No retroactive switching without founder review.
 * 
 * Options (LOCKED):
 * - Individual
 * - Family
 * - School / Institution / Organization
 * - Business
 * 
 * Each leads to its own explanation page, then cost/approval flow.
 */

export default function MembershipIntent() {
  const navigate = useNavigate();

  const options = [
    {
      id: 'individual',
      title: 'Individual',
      description: 'Coordinate with groups and communities in your area.',
      icon: User,
      color: '#7A8F5B', // Primary Green
    },
    {
      id: 'family',
      title: 'Family',
      description: 'Manage household coordination across family members.',
      icon: Users,
      color: '#D98A3A', // Secondary Orange
    },
    {
      id: 'institution',
      title: 'School / Institution / Organization',
      description: 'Create a Commons for a school, nonprofit, or program.',
      icon: Building2,
      color: '#8B6A4F', // Earth Brown
    },
    {
      id: 'business',
      title: 'Business',
      description: 'Participate in category sponsorship. Subject to approval.',
      icon: Briefcase,
      color: '#6B7280', // Neutral Gray
    },
  ];

  const handleSelect = (optionId) => {
    navigate(`/membership/${optionId}`);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="flex items-center p-4">
        <button
          onClick={() => navigate('/auth')}
          className="p-2 rounded-lg"
          style={{ color: 'var(--fy-text-secondary)' }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </header>

      {/* Content */}
      <main className="px-6 pb-12">
        <div className="max-w-md mx-auto">
          {/* Title */}
          <h1 
            className="text-2xl font-bold text-center mb-2"
            style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
          >
            Choose how to enter
          </h1>
          <p 
            className="text-center text-sm mb-8"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            Select the option that best describes you.
          </p>

          {/* Options */}
          <div className="space-y-4">
            {options.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className="w-full p-5 rounded-2xl text-left transition-all duration-200 hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: 'var(--fy-surface-card)',
                    border: '2px solid var(--fy-border-subtle)',
                  }}
                  data-testid={`membership-intent-${option.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${option.color}15` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: option.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 
                        className="font-bold text-lg mb-1"
                        style={{ color: 'var(--fy-text-primary)' }}
                      >
                        {option.title}
                      </h3>
                      <p 
                        className="text-sm"
                        style={{ color: 'var(--fy-text-secondary)' }}
                      >
                        {option.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Foundation statement */}
          <p 
            className="text-center text-xs mt-8 px-4"
            style={{ color: 'var(--fy-text-disabled)' }}
          >
            No tracking. No targeting. No engagement metrics.
          </p>
        </div>
      </main>
    </div>
  );
}
